/**
 * `mmorpg_game_balance`, as far as the stat container needs it.
 *
 * Only the level-scaling curves and `MAX_LEVEL` matter here; the rest of that registry is
 * drop rates, backpack sizes and mob scaling. Craft to Exile 2 runs `original_balance`, which
 * `@cte2/schema` already knows as `DEFAULT_BALANCE_ID`.
 */
import { CATEGORY, DEFAULT_BALANCE_ID, entry } from "@cte2/schema";
/**
 * `LevelScalingConfig.getMultiFor` — what one of these curves is worth at a level.
 *
 *     public float getMultiFor(int lvl) {
 *         if (cap_to_max_lvl) { lvl = Mth.clamp(lvl, 1, max); }
 *         return base_scaling + per_lvl_scaling * (lvl - 1);
 *     }
 *
 * Here rather than beside its one caller because the spell tooltip prints the same mana figure
 * the cast pays, and the two arriving at it by different arithmetic is the kind of disagreement
 * nobody notices until a number on a card is off by a level's worth.
 *
 * Note the cap applies to the *level*, not to the result.
 */
export function levelScalingMulti(curve, level, maxLevel) {
    const lvl = curve.capToMaxLvl ? Math.min(Math.max(level, 1), maxLevel) : level;
    return curve.baseScaling + curve.perLevelScaling * (lvl - 1);
}
/** Which `LevelScalingConfig` each `StatScaling` reads (StatScaling.java:5-43). */
const CONFIG_KEY = {
    NONE: null,
    NORMAL: "NORMAL_STAT_SCALING",
    CORE: "CORE_STAT_SCALING",
    STAT_REQ: "STAT_REQ_SCALING",
    MOB_DAMAGE: "MOB_DAMAGE_SCALING",
    SLOW: "SLOW_STAT_SCALING",
};
/**
 * `GameBalanceConfig`'s own field initialisers, used when the registry entry omits a curve.
 * These are the jar defaults, not the pack's — the pack overrides `original_balance` wholesale
 * and every curve is present there, so this is a floor rather than something load-bearing.
 */
const FALLBACK = {
    NORMAL_STAT_SCALING: { baseScaling: 1, perLevelScaling: 0.2, capToMaxLvl: false },
    CORE_STAT_SCALING: { baseScaling: 1, perLevelScaling: 0.05, capToMaxLvl: true },
    SLOW_STAT_SCALING: { baseScaling: 1, perLevelScaling: 0.01, capToMaxLvl: true },
    STAT_REQ_SCALING: { baseScaling: 2, perLevelScaling: 0.2, capToMaxLvl: true },
    MOB_DAMAGE_SCALING: { baseScaling: 1, perLevelScaling: 0.025, capToMaxLvl: false },
};
/** `GameBalanceConfig.MANA_COST_SCALING`, which is not a `StatScaling` and so is not in `CONFIG_KEY`. */
const MANA_COST_FALLBACK = { baseScaling: 1, perLevelScaling: 0.2, capToMaxLvl: true };
/**
 * Cached per snapshot and balance id. A `Balance` closes over its curves and never mutates,
 * so sharing one is safe — and `multiFor` is called once per FLAT modifier, which for a geared
 * character is thousands of times per calculation.
 */
const BALANCE_CACHE = new WeakMap();
export function balance(snapshot, balanceId = DEFAULT_BALANCE_ID) {
    let byId = BALANCE_CACHE.get(snapshot);
    if (!byId) {
        byId = new Map();
        BALANCE_CACHE.set(snapshot, byId);
    }
    const cached = byId.get(balanceId);
    if (cached)
        return cached;
    const built = buildBalance(snapshot, balanceId);
    byId.set(balanceId, built);
    return built;
}
function buildBalance(snapshot, balanceId) {
    const data = entry(snapshot, CATEGORY.gameBalance, balanceId)?.data ?? {};
    const maxLevel = numberAt(data, "MAX_LEVEL") ?? 100;
    const curves = new Map();
    for (const key of Object.keys(FALLBACK)) {
        curves.set(key, readCurve(data[key]) ?? FALLBACK[key]);
    }
    return {
        id: balanceId,
        maxLevel,
        maxBonusSpellLevels: numberAt(data, "MAX_BONUS_SPELL_LEVELS") ?? 5,
        damageReductionPerChain: numberAt(data, "DMG_REDUCT_PER_CHAIN") ?? 0.2,
        minChainDamage: numberAt(data, "MIN_CHAIN_DMG") ?? 0.2,
        minSpellCooldownMulti: numberAt(data, "MIN_SPELL_COOLDOWN_MULTI") ?? 0.2,
        globalCooldownTicks: numberAt(data, "GLOBAL_COOLDOWN_TICKS") ?? 3,
        channelSpeedTransfer: numberAt(data, "CHANNEL_GENERAL_SPEED_TRANSFER") ?? 1,
        manaCostScaling: readCurve(data["MANA_COST_SCALING"]) ?? MANA_COST_FALLBACK,
        mobDmgPowerScaling: numberAt(data, "MOB_DMG_POWER_SCALING") ?? 1.003,
        mobDmgPowerScalingBase: numberAt(data, "MOB_DMG_POWER_SCALING_BASE") ?? 1,
        hpMobBonusPerMapTier: numberAt(data, "HP_MOB_BONUS_PER_MAP_TIER") ?? 0.067,
        dmgMobBonusPerMapTier: numberAt(data, "DMG_MOB_BONUS_PER_MAP_TIER") ?? 0.01,
        multiFor(scaling, level) {
            const key = CONFIG_KEY[scaling];
            if (key === null)
                return 1;
            const curve = curves.get(key);
            const lvl = curve.capToMaxLvl ? Math.min(Math.max(level, 1), maxLevel) : level;
            return curve.baseScaling + curve.perLevelScaling * (lvl - 1);
        },
    };
}
function readCurve(node) {
    if (node === null || typeof node !== "object" || Array.isArray(node))
        return undefined;
    const n = node;
    const baseScaling = numberAt(n, "base_scaling");
    const perLevelScaling = numberAt(n, "per_level_scaling");
    if (baseScaling === undefined || perLevelScaling === undefined)
        return undefined;
    return {
        baseScaling,
        perLevelScaling,
        capToMaxLvl: typeof n["cap_to_max_lvl"] === "boolean" ? n["cap_to_max_lvl"] : true,
    };
}
function numberAt(node, key) {
    const v = node[key];
    return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}
//# sourceMappingURL=balance.js.map